import { useTranslation } from "react-i18next"
import dayjs from "dayjs"
import { UsageChart } from "components/Analytics/UsageChart/UsageChart"
import { ProductDistributionChart } from "components/Analytics/ProductDistributionChart/ProductDistributionChart"
import { EnhancedAnalyticsSummary } from "components/Analytics/EnhancedAnalyticsSummary/EnhancedAnalyticsSummary"
import { TimeBasedComparison } from "components/Analytics/TimeBasedComparison/TimeBasedComparison"
import { ConversionFunnelChart } from "components/Analytics/ConversionFunnelChart/ConversionFunnelChart"
import { MetricsChart } from "components/Analytics/MetricsChart/MetricsChart"
import { useAnalyticsQuery } from "hooks/query/useAnalyticsQuery"
import { useMerchantsQuery } from "hooks/query/useMerchantQuery"
import { useAppSelector } from "hooks/storeHooks"
import { selectUserInfo } from "store/user/userSlice"
import { useCurrentId } from "hooks/useCurrentId"
import { Loader } from "@strapi/design-system"
import { MerchantSelector } from "components/MerchantSelector/MerchantSelector"
import { DateRangePicker } from "components/Analytics/DateRangePicker"
import { Download } from "lucide-react"
import { downloadFile } from "utils/fileReader"
import { cn } from "@/lib/utils"
import { useState, useEffect, useCallback } from "react"

export const AnalyticsPage = () => {
  const { t } = useTranslation()
  const userInfo = useAppSelector(selectUserInfo)
  const urlMerchantId = useCurrentId()

  // Determine if user is admin
  const isAdmin = userInfo?.groups?.includes("Admin") || false

  // Only fetch merchants for admin users
  const {
    data: merchants = [],
    isLoading: merchantsLoading,
    error: merchantsError,
  } = useMerchantsQuery(isAdmin)

  const [selectedMerchant, setSelectedMerchant] = useState("")
  const [selectedMerchantForDisplay, setSelectedMerchantForDisplay] = useState("")

  const today = dayjs().format("YYYY-MM-DD")
  const last30Days = dayjs().subtract(29, "day").format("YYYY-MM-DD")
  const [dateStart, setDateStart] = useState(last30Days)
  const [dateEnd, setDateEnd] = useState(today)

  const handleDateRangeChange = (start: string, end: string) => {
    setDateStart(start)
    setDateEnd(end)
  }

  // For non-admin users, automatically set their merchantId
  useEffect(() => {
    if (!isAdmin && userInfo?.merchantId && !selectedMerchant) {
      setSelectedMerchant(userInfo.merchantId)
    }
  }, [isAdmin, userInfo?.merchantId, selectedMerchant])

  // Update selected merchant when URL changes (for admin users)
  useEffect(() => {
    if (isAdmin && urlMerchantId) {
      setSelectedMerchant(urlMerchantId)
      const merchantInfo = merchants?.find((m) => m.id === urlMerchantId)
      if (merchantInfo)
        setSelectedMerchantForDisplay(`${merchantInfo.name} (${merchantInfo.domain})`)
    }
  }, [isAdmin, urlMerchantId, merchants])

  // Query analytics data for the selected merchant (only when merchant is selected)
  const {
    data: analyticsData,
    isLoading,
    error,
  } = useAnalyticsQuery({
    merchantId: selectedMerchant,
    dateStart,
    dateEnd,
  })

  // Analytics data is now parsed in the query hook
  const parsedData: any = analyticsData || null

  // Handle different data structures
  let widgetAnalytics = []
  let productAnalytics = []
  let _weeklyData = []
  let dataState = { hasData: false, hasBillingCycle: false, message: "", title: "" }

  if (parsedData)
    if (Array.isArray(parsedData)) {
      // If it's an array of analytics items (direct from GraphQL)

      // Transform server data to match component expectations
      const transformedData = parsedData.map((item) => ({
        date: item.date,
        hostname: item.hostname,
        unique_users: item.unique_users,
        engaged_users: item.engaged_users,
        app_opened: item.app_open_events, // Map app_open_events to app_opened
        vto_events: item.vto_events,
        vto_users: item.vto_users,
        add_to_cart: item.add_to_cart_events, // Map add_to_cart_events to add_to_cart
        add_to_cart_users: item.add_to_cart_users,
        add_to_cart_total_skus: item.add_to_cart_total_skus,
        photo_upload_click: item.photo_uploaded_events, // Map photo_uploaded_events to photo_upload_click
        // Keep product-specific fields for product analytics
        product_title: item.product_title,
        merchant_id: item.merchant_id,
        user_engagement_secs: item.user_engagement_secs,
      }))

      widgetAnalytics = transformedData
      productAnalytics = transformedData
      dataState = { hasData: parsedData.length > 0, hasBillingCycle: false, message: "", title: "" }
    }
    // If it's the expected structured object
    else if (parsedData.widgetAnalytics || parsedData.productAnalytics) {
      widgetAnalytics = parsedData.widgetAnalytics || []
      productAnalytics = parsedData.productAnalytics || []
      _weeklyData = parsedData.weeklyData || []
      dataState = parsedData.dataState || {
        hasData: false,
        hasBillingCycle: false,
        message: "",
        title: "",
      }
    }

  // Generate usage data from product analytics (cumulative over selected date range)
  const generateUsageDataFromProducts = () => {
    const start = dayjs(dateStart)
    const end = dayjs(dateEnd)
    const daysDiff = end.diff(start, "day") + 1

    const dateRange = Array.from({ length: daysDiff }, (_, i) =>
      start.add(i, "day").format("YYYY-MM-DD")
    )

    let cumulativeUsage = 0
    const usageData = dateRange.map((date) => {
      const dailyUsage = productAnalytics
        .filter((product) => product.date === date)
        .reduce((sum, product) => sum + product.vto_events, 0)

      cumulativeUsage += dailyUsage

      return {
        date: dayjs(date).format("MMM D"),
        usage: cumulativeUsage,
        extrapolatedUsage: null,
      }
    })

    return usageData
  }

  const usageData = generateUsageDataFromProducts()
  const totalUsage = usageData[usageData.length - 1]?.usage || 0

  // Get selected merchant info
  const selectedMerchantInfo = merchants?.find((m) => m.id === selectedMerchant)

  const exportToCSV = useCallback(() => {
    if (!widgetAnalytics.length && !productAnalytics.length) return

    const headers = [
      "Date",
      "Hostname",
      "Product Title",
      "Unique Users",
      "Engaged Users",
      "App Opened",
      "VTO Events",
      "VTO Users",
      "Add to Cart Events",
      "Add to Cart Users",
      "Add to Cart Total SKUs",
      "Photo Upload Events",
      "User Engagement Seconds",
      "Merchant ID",
    ]

    const rows = widgetAnalytics.map((item) => [
      item.date || "",
      item.hostname || "",
      item.product_title || "",
      item.unique_users || 0,
      item.engaged_users || 0,
      item.app_opened || 0,
      item.vto_events || 0,
      item.vto_users || 0,
      item.add_to_cart || 0,
      item.add_to_cart_users || 0,
      item.add_to_cart_total_skus || 0,
      item.photo_upload_click || 0,
      item.user_engagement_secs || 0,
      item.merchant_id || "",
    ])

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n")

    const filename = `analytics_${dateStart}_to_${dateEnd}_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}.csv`
    downloadFile(csvContent, filename, "text/csv;charset=utf-8;")
  }, [widgetAnalytics, productAnalytics, dateStart, dateEnd])

  // Handle merchant selection - automatically set selected merchant
  const handleMerchantSelect = (merchantDisplay: string) => {
    setSelectedMerchantForDisplay(merchantDisplay)

    // Check if it's a direct merchant ID search
    if (merchantDisplay.startsWith("Merchant ID: ")) {
      const merchantId = merchantDisplay.replace("Merchant ID: ", "")
      setSelectedMerchant(merchantId)
      return
    }

    // Find the merchant and automatically set it as selected
    const merchant = merchants?.find((m) => `${m.name} (${m.domain})` === merchantDisplay)
    if (merchant) setSelectedMerchant(merchant.id)
  }

  // Show loading state
  if (isLoading || (isAdmin && merchantsLoading))
    return (
      <div className="min-h-screen bg-dark-primary">
        <div className="bg-dark-secondary border-b border-gray-700 px-4 sm:px-6 py-4">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-xl sm:text-2xl lg:text-3xl analytics-title">
              {t("analytics.dashboard.title")}
            </h1>
            <p className="analytics-subtitle text-sm sm:text-base lg:text-lg">
              {t("analytics.dashboard.subtitle")}
            </p>
            {selectedMerchantInfo && selectedMerchantInfo.name && selectedMerchantInfo.domain && (
              <p className="text-xs sm:text-sm text-gray-300 mt-2">
                {t("analytics.merchant")}: {selectedMerchantInfo.name} (
                {selectedMerchantInfo.domain})
              </p>
            )}
          </div>
        </div>
        <div className="p-4 sm:p-6">
          <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
            {isAdmin && (
              <MerchantSelector
                merchants={merchants || []}
                onMerchantSelect={handleMerchantSelect}
                selectedMerchantForDisplay={selectedMerchantForDisplay}
              />
            )}
            <div className="min-h-[300px] sm:min-h-[400px] flex items-center justify-center flex-col">
              <Loader />
              <p className="text-gray-300 text-center mt-4 text-sm sm:text-base">
                {t("analytics.loadingDescription")}
              </p>
            </div>
          </div>
        </div>
      </div>
    )

  // Show error state
  if (error || (isAdmin && merchantsError))
    return (
      <div className="min-h-screen bg-dark-primary">
        <div className="bg-dark-secondary border-b border-gray-700 px-4 sm:px-6 py-4">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-xl sm:text-2xl lg:text-3xl analytics-title">
              {t("analytics.dashboard.title")}
            </h1>
            <p className="analytics-subtitle text-sm sm:text-base lg:text-lg">
              {t("analytics.dashboard.subtitle")}
            </p>
            {selectedMerchantInfo && selectedMerchantInfo.name && selectedMerchantInfo.domain && (
              <p className="text-xs sm:text-sm text-gray-300 mt-2">
                {t("analytics.merchant")}: {selectedMerchantInfo.name} (
                {selectedMerchantInfo.domain})
              </p>
            )}
          </div>
        </div>
        <div className="p-4 sm:p-6">
          <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
            {isAdmin && (
              <MerchantSelector
                merchants={merchants || []}
                onMerchantSelect={handleMerchantSelect}
                selectedMerchantForDisplay={selectedMerchantForDisplay}
              />
            )}
            <div className="min-h-[300px] sm:min-h-[400px] flex items-center justify-center flex-col">
              <h2 className="text-lg sm:text-xl font-semibold text-center mb-2 text-white">
                {t("analytics.error.title")}
              </h2>
              <p className="text-gray-300 text-center mb-4 text-sm sm:text-base">
                {t("analytics.error.description")}
              </p>
            </div>
          </div>
        </div>
      </div>
    )

  // Show empty state if no data
  if (!dataState.hasData && (!parsedData || (Array.isArray(parsedData) && parsedData.length === 0)))
    return (
      <div className="min-h-screen bg-dark-primary">
        <div className="bg-dark-secondary border-b border-gray-700 px-4 sm:px-6 py-4">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-xl sm:text-2xl lg:text-3xl analytics-title">
              {t("analytics.dashboard.title")}
            </h1>
            <p className="analytics-subtitle text-sm sm:text-base lg:text-lg">
              {t("analytics.dashboard.subtitle")}
            </p>
            {selectedMerchantInfo && selectedMerchantInfo.name && selectedMerchantInfo.domain && (
              <p className="text-xs sm:text-sm text-gray-300 mt-2">
                {t("analytics.merchant")}: {selectedMerchantInfo.name} (
                {selectedMerchantInfo.domain})
              </p>
            )}
          </div>
        </div>
        <div className="p-4 sm:p-6">
          <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
            {isAdmin && (
              <MerchantSelector
                merchants={merchants || []}
                onMerchantSelect={handleMerchantSelect}
                selectedMerchantForDisplay={selectedMerchantForDisplay}
              />
            )}
            <div className="min-h-[300px] sm:min-h-[400px] flex items-center justify-center flex-col">
              <h2 className="text-lg sm:text-xl font-semibold text-center mb-2 text-white">
                {t("analytics.empty.noData")}
              </h2>
              <p className="text-gray-300 text-center mb-4 text-sm sm:text-base">
                {t("analytics.empty.noDataDescription")}
              </p>
            </div>
          </div>
        </div>
      </div>
    )

  return (
    <div className="min-h-screen bg-dark-primary">
      <div className="bg-dark-secondary border-b border-gray-700 px-4 sm:px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1">
              <h1 className="text-xl sm:text-2xl lg:text-3xl analytics-title">
                {t("analytics.dashboard.title")}
              </h1>
              <p className="analytics-subtitle text-sm sm:text-base lg:text-lg">
                {t("analytics.dashboard.subtitle")}
              </p>
              {selectedMerchantInfo && selectedMerchantInfo.name && selectedMerchantInfo.domain && (
                <p className="text-xs sm:text-sm text-gray-300 mt-2">
                  {t("analytics.merchant")}: {selectedMerchantInfo.name} ({selectedMerchantInfo.domain})
                </p>
              )}
            </div>
            {selectedMerchant && (widgetAnalytics.length > 0 || productAnalytics.length > 0) && (
              <button
                type="button"
                onClick={exportToCSV}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700",
                  "text-white rounded-md transition-colors",
                  "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800",
                  "text-sm font-medium"
                )}
              >
                <Download className="w-4 h-4" />
                {t("analytics.exportCSV")}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6">
        <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
          {isAdmin && (
            <MerchantSelector
              merchants={merchants || []}
              onMerchantSelect={handleMerchantSelect}
              selectedMerchantForDisplay={selectedMerchantForDisplay}
            />
          )}

          {!selectedMerchant ? (
            <div className="bg-dark-secondary rounded-lg border border-gray-700 p-6 text-center">
              <h3 className="text-lg font-semibold text-white mb-2">
                {t("analytics.selectMerchantToView")}
              </h3>
              <p className="text-gray-300">{t("analytics.selectMerchantDescription")}</p>
            </div>
          ) : (
            <>
              {/* Date Range Picker */}
              <div className="bg-dark-secondary rounded-lg border border-gray-700 p-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">
                      {t("analytics.dateRange")}
                    </h3>
                    <p className="text-sm text-gray-300">{t("analytics.selectDateRangeDescription")}</p>
                  </div>
                  <DateRangePicker
                    startDate={dateStart}
                    endDate={dateEnd}
                    onChange={handleDateRangeChange}
                  />
                </div>
              </div>

              {/* Usage Tracking with Quick Stats */}
              <UsageChart
                data={usageData}
                threshold={10000}
                currentUsage={totalUsage}
                subscriptionName="Analytics Dashboard"
                startOfCycle={dayjs(dateStart).format("MMM D")}
                endOfCycle={dayjs(dateEnd).format("MMM D")}
                showOverlay={false}
              />

              {/* Key Metrics Summary */}
              <EnhancedAnalyticsSummary
                analyticsItems={widgetAnalytics}
                productAnalytics={productAnalytics}
                startOfCycle={widgetAnalytics[0]?.date || dateStart}
                showOverlay={false}
              />

              {/* Product Distribution Chart */}
              <ProductDistributionChart productAnalytics={productAnalytics} showOverlay={false} />

              {/* Conversion Funnel Chart */}
              <ConversionFunnelChart
                analyticsItems={widgetAnalytics}
                startOfCycle={widgetAnalytics[0]?.date || dateStart}
                showOverlay={false}
              />

              {/* Time-based Analysis */}
              <TimeBasedComparison analyticsItems={widgetAnalytics} showOverlay={false} />

              {/* Detailed Metrics Chart */}
              <MetricsChart
                analyticsItems={widgetAnalytics}
                productAnalytics={productAnalytics}
                showOverlay={false}
              />
            </>
          )}
        </div>
      </div>
    </div>
  )
}
